import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { ExternalApiConfig } from '../../../../shared/services/models/ExternalApiConfig';
import { DealerState, UpdateDealerFlagsAction } from '../../../../shared/state/DealerState';
import { NOTIFICATION_RESOURCE_TYPES } from '../../../../shared/models/notification-resource-type.model';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';

interface DynamicConfigField {
  key: string;
  label: string;
  type: 'boolean' | 'number' | 'string' | 'array';
}

@Component({
  selector: 'app-administration-screen',
  templateUrl: './administration-screen.component.html',
  styleUrls: ['./administration-screen.component.scss'],
  standalone: false
})
export class AdministrationScreenComponent implements OnInit {
  readonly maxExternalApiNumber = 1000000;
  featureFlagsForm: FormGroup;
  externalApiConfigForm: FormGroup;
  featureFlagKeys: string[] = [];
  externalApiConfigFields: DynamicConfigField[] = [];
  selectedTabIndex = 0;
  loading = false;
  saving = false;
  externalApiLoading = false;
  externalApiSaving = false;
  externalApiConfigAvailable = false;
  accessibleEntityInput = '';
  dynamicArrayInputs: Record<string, string> = {};
  newDynamicArrayItems: Record<string, string[]> = {};
  private dynamicArrayItemEnabled: Record<string, boolean[]> = {};
  featureFlagsExpanded = true;
  externalApiConfigExpanded = true;
  private initialFlags: { [key: string]: boolean } = {};
  private initialExternalApiConfig: ExternalApiConfig = this.emptyExternalApiConfig();

  // Notification type preferences have a dedicated UI. The global notification
  // gate intentionally remains in Feature Flags with all other feature gates.
  private readonly reservedFlagKeys: string[] = NOTIFICATION_RESOURCE_TYPES.map((definition) => definition.preferenceFlag);

  public translationService = inject(TranslationService);
  private readonly featureFlagsService = inject(FeatureflagsService);
  public readonly alertService = inject(AlertService);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);

  get dealerId(): string {
    return this.store.selectSnapshot(DealerState.currentDealer)?.id ?? '';
  }

  get hasUnsavedChanges(): boolean {
    return this.featureFlagKeys.some((key) => this.isFlagDirty(key));
  }

  get isFeatureFlagsTabSelected(): boolean {
    return this.selectedTabIndex === 0;
  }

  get hasExternalApiConfigChanges(): boolean {
    return (
      JSON.stringify(this.sortExternalApiConfigValue(this.getPersistedExternalApiConfig())) !==
      JSON.stringify(this.sortExternalApiConfigValue(this.initialExternalApiConfig))
    );
  }

  isExternalApiFieldDirty(fieldKey: string): boolean {
    const currentValue = this.getPersistedExternalApiConfig()[fieldKey];
    const initialValue = this.initialExternalApiConfig[fieldKey];
    return JSON.stringify(this.sortExternalApiConfigValue(currentValue)) !== JSON.stringify(this.sortExternalApiConfigValue(initialValue));
  }

  get hasUncheckedDynamicArrayItems(): boolean {
    return Object.values(this.dynamicArrayItemEnabled).some((enabledStates) => enabledStates.some((enabled) => !enabled));
  }

  get accessibleEntities(): FormArray {
    return (this.externalApiConfigForm?.get('accessibleEntities') as FormArray) ?? this.fb.array([]);
  }

  // UIUX presentation grouping only (no business/API logic change): the dynamic
  // external API config fields are split into booleans, scalar (number/string),
  // and array groups so the template can render booleans first, then scalar
  // inputs in the compact grid, and all arrays together in a section at the
  // bottom — matching the Plant Setup screen's field ordering/layout.
  get externalApiBooleanFields(): DynamicConfigField[] {
    return this.externalApiConfigFields.filter((field) => field.type === 'boolean');
  }

  get externalApiScalarFields(): DynamicConfigField[] {
    return this.externalApiConfigFields.filter((field) => field.type === 'number' || field.type === 'string');
  }

  get externalApiArrayFields(): DynamicConfigField[] {
    return this.externalApiConfigFields.filter((field) => field.type === 'array');
  }

  ngOnInit(): void {
    this.featureFlagsForm = this.fb.group({});
    this.externalApiConfigForm = this.createExternalApiConfigForm(this.emptyExternalApiConfig());
    this.loadFeatureFlags().catch(() => undefined);
    this.loadExternalApiConfig().catch(() => undefined);
  }

  async loadExternalApiConfig(): Promise<void> {
    this.externalApiLoading = true;
    try {
      const result = await this.featureFlagsService.fetchAllExternalApiConfig(this.dealerId);
      this.setExternalApiConfig(result?.config ?? this.emptyExternalApiConfig());
    } catch {
      this.externalApiConfigAvailable = false;
      this.alertService.showAlert({
        type: AlertType.critical,
        content: 'Failed to load external api config.'
      } as Alert);
    } finally {
      this.externalApiLoading = false;
    }
  }

  async saveExternalApiConfig(): Promise<void> {
    if (!this.externalApiConfigAvailable || this.externalApiConfigForm.invalid || !this.hasExternalApiConfigChanges) return;

    this.externalApiSaving = true;
    try {
      const config = this.normalizeExternalApiConfig(this.getPersistedExternalApiConfig());
      const result = await this.featureFlagsService.saveExternalApiConfig(this.dealerId, config);
      this.setExternalApiConfig(result?.config ?? config);
      this.alertService.showAlert({
        type: AlertType.success,
        content: 'External API configuration saved successfully.'
      } as Alert);
    } catch {
      this.alertService.showAlert({
        type: AlertType.critical,
        content: 'Failed to save external API configuration.'
      } as Alert);
    } finally {
      this.externalApiSaving = false;
    }
  }

  resetExternalApiConfigChanges(): void {
    this.setExternalApiConfig(this.initialExternalApiConfig);
  }

  addAccessibleEntity(): void {
    this.dynamicArrayInputs.accessibleEntities = this.accessibleEntityInput;
    this.addDynamicArrayItem('accessibleEntities');
  }

  removeAccessibleEntity(index: number): void {
    this.removeDynamicArrayItem('accessibleEntities', index);
  }

  addDynamicArrayItem(fieldKey: string): void {
    const rawValue = this.dynamicArrayInputs[fieldKey];
    const trimmedValue = typeof rawValue === 'string' ? rawValue.trim() : '';
    const value = fieldKey === 'accessibleEntities' ? trimmedValue.toUpperCase() : trimmedValue;
    if (!value) return;

    const array = this.externalApiConfigForm.get(fieldKey);
    if (!(array instanceof FormArray)) return;

    if (array.controls.some((control) => String(control.value).toLowerCase() === value.toLowerCase())) {
      return;
    }

    array.insert(0, this.createExternalApiArrayItemControl(fieldKey, value));
    array.markAsDirty();
    this.dynamicArrayItemEnabled[fieldKey] = [true, ...(this.dynamicArrayItemEnabled[fieldKey] ?? [])];
    this.newDynamicArrayItems[fieldKey] = [value, ...(this.newDynamicArrayItems[fieldKey] ?? [])];
    this.dynamicArrayInputs[fieldKey] = '';
    this.accessibleEntityInput = '';
  }

  removeDynamicArrayItem(fieldKey: string, index: number): void {
    const array = this.externalApiConfigForm.get(fieldKey);
    if (!(array instanceof FormArray)) return;

    const removedValue = this.stringifyExternalApiValue(array.at(index)?.value);
    array.removeAt(index);
    array.markAsDirty();
    this.dynamicArrayItemEnabled[fieldKey] = (this.dynamicArrayItemEnabled[fieldKey] ?? []).filter((_, itemIndex) => itemIndex !== index);
    this.newDynamicArrayItems[fieldKey] = (this.newDynamicArrayItems[fieldKey] ?? []).filter(
      (value) => value.toLowerCase() !== removedValue.toLowerCase()
    );
  }

  isDynamicArrayItemEnabled(fieldKey: string, index: number): boolean {
    return this.dynamicArrayItemEnabled[fieldKey]?.[index] ?? true;
  }

  setDynamicArrayItemEnabled(fieldKey: string, index: number, enabled: boolean): void {
    const enabledStates = [...(this.dynamicArrayItemEnabled[fieldKey] ?? [])];
    enabledStates[index] = enabled;
    this.dynamicArrayItemEnabled[fieldKey] = enabledStates;
  }

  getExternalApiArray(fieldKey: string): FormArray {
    return (this.externalApiConfigForm?.get(fieldKey) as FormArray) ?? this.fb.array([]);
  }

  private createExternalApiConfigForm(config: ExternalApiConfig): FormGroup {
    const controls: Record<string, AbstractControl> = {};
    this.getSortedExternalApiConfigKeys(config).forEach((key) => {
      controls[key] = this.createExternalApiControl(config[key]);
    });
    this.externalApiConfigFields = this.getExternalApiConfigFields(config);
    this.dynamicArrayItemEnabled = this.getSortedExternalApiConfigKeys(config).reduce<Record<string, boolean[]>>((result, key) => {
      if (Array.isArray(config[key])) {
        result[key] = (config[key] as unknown[]).map(() => true);
      }
      return result;
    }, {});
    return this.fb.group(controls);
  }

  private setExternalApiConfig(config: Partial<ExternalApiConfig>): void {
    const normalized = this.normalizeExternalApiConfig(config);
    this.initialExternalApiConfig = normalized;
    this.externalApiConfigAvailable = Object.keys(normalized).length > 0;
    this.externalApiConfigForm = this.createExternalApiConfigForm(normalized);
    this.dynamicArrayInputs = {};
    this.newDynamicArrayItems = {};
    this.accessibleEntityInput = '';
  }

  isNewDynamicArrayItem(fieldKey: string, value: unknown): boolean {
    const comparableValue = this.stringifyExternalApiValue(value).toLowerCase();
    return (this.newDynamicArrayItems[fieldKey] ?? []).some((newValue) => newValue.toLowerCase() === comparableValue);
  }

  private normalizeExternalApiConfig(config: Partial<ExternalApiConfig>): ExternalApiConfig {
    const normalized: ExternalApiConfig = {};
    Object.keys(config ?? {}).forEach((key) => {
      normalized[key] = this.cloneExternalApiConfigValue((config as Record<string, unknown>)[key]) as ExternalApiConfig[string];
    });
    return normalized;
  }

  private cloneExternalApiConfigValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.cloneExternalApiConfigValue(item));
    }

    if (value !== null && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((result, [key, nestedValue]) => {
        result[key] = this.cloneExternalApiConfigValue(nestedValue);
        return result;
      }, {});
    }

    return value;
  }

  private sortExternalApiConfigValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.sortExternalApiConfigValue(item));
    }

    if (value !== null && typeof value === 'object') {
      return Object.keys(value as Record<string, unknown>)
        .sort((leftKey, rightKey) => leftKey.localeCompare(rightKey))
        .reduce<Record<string, unknown>>((result, key) => {
          result[key] = this.sortExternalApiConfigValue((value as Record<string, unknown>)[key]);
          return result;
        }, {});
    }

    return value;
  }

  private createExternalApiControl(value: unknown): AbstractControl {
    if (Array.isArray(value)) {
      return this.fb.array((value as unknown[]).map((item) => this.createExternalApiItemControl(item)));
    }

    const primitiveControl = this.createExternalApiPrimitiveControl(value);
    if (primitiveControl) {
      return primitiveControl;
    }

    if (value !== null && typeof value === 'object') {
      const nestedControls: Record<string, AbstractControl> = {};
      this.getSortedExternalApiConfigKeys(value as ExternalApiConfig).forEach((key) => {
        nestedControls[key] = this.createExternalApiControl((value as Record<string, unknown>)[key]);
      });
      return this.fb.group(nestedControls);
    }

    return this.fb.control('');
  }

  private createExternalApiItemControl(value: unknown): AbstractControl {
    const primitiveControl = this.createExternalApiPrimitiveControl(value);
    if (primitiveControl) {
      return primitiveControl;
    }

    return this.fb.control(this.stringifyExternalApiValue(value));
  }

  private createExternalApiPrimitiveControl(value: unknown): AbstractControl | null {
    if (typeof value === 'boolean') {
      return this.fb.control(value);
    }

    if (typeof value === 'number') {
      const numberValue = Number.isFinite(value) ? value : 0;
      return this.fb.control(numberValue, [Validators.min(0), Validators.max(this.maxExternalApiNumber)]);
    }

    if (typeof value === 'string') {
      return this.fb.control(value);
    }

    return null;
  }

  private stringifyExternalApiValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return `${value}`;
    }

    return JSON.stringify(value) ?? '';
  }

  private createExternalApiArrayItemControl(fieldKey: string, rawValue: string): AbstractControl {
    const itemType = this.resolveArrayType(fieldKey);
    if (itemType === 'number') {
      return this.fb.control(Number(rawValue) || 0, [Validators.min(0), Validators.max(this.maxExternalApiNumber)]);
    }

    if (itemType === 'boolean') {
      return this.fb.control(rawValue.toLowerCase() === 'true');
    }

    return this.fb.control(rawValue);
  }

  private getPersistedExternalApiConfig(): ExternalApiConfig {
    const rawConfig = this.externalApiConfigForm?.getRawValue() ?? {};
    const persistedConfig = this.normalizeExternalApiConfig(rawConfig);

    this.externalApiArrayFields.forEach((field) => {
      const values = persistedConfig[field.key];
      if (Array.isArray(values)) {
        const enabledStates = this.dynamicArrayItemEnabled[field.key] ?? [];
        persistedConfig[field.key] = values.filter((_, index) => enabledStates[index] !== false);
      }
    });

    return persistedConfig;
  }

  private resolveArrayType(fieldKey: string): 'string' | 'number' | 'boolean' {
    const arrayControl = this.externalApiConfigForm.get(fieldKey);
    if (arrayControl instanceof FormArray && arrayControl.length > 0) {
      const firstValue = arrayControl.at(0)?.value;
      if (typeof firstValue === 'number') return 'number';
      if (typeof firstValue === 'boolean') return 'boolean';
    }

    return 'string';
  }

  private getExternalApiConfigFields(config: ExternalApiConfig): DynamicConfigField[] {
    return this.getSortedExternalApiConfigKeys(config).map((key) => ({
      key,
      label: this.formatExternalApiConfigLabel(key),
      type: this.getExternalApiConfigFieldType(config[key])
    }));
  }

  private getExternalApiConfigFieldType(value: unknown): DynamicConfigField['type'] {
    if (Array.isArray(value)) {
      return 'array';
    }

    if (typeof value === 'boolean') {
      return 'boolean';
    }

    if (typeof value === 'number') {
      return 'number';
    }

    return 'string';
  }

  private getSortedExternalApiConfigKeys(config: Record<string, unknown>): string[] {
    return Object.keys(config).sort((leftKey, rightKey) => {
      const leftLabel = this.formatExternalApiConfigLabel(leftKey);
      const rightLabel = this.formatExternalApiConfigLabel(rightKey);
      const labelOrder = leftLabel.localeCompare(rightLabel, undefined, { sensitivity: 'base' });
      return labelOrder !== 0 ? labelOrder : leftKey.localeCompare(rightKey);
    });
  }

  private emptyExternalApiConfig(): ExternalApiConfig {
    return {};
  }

  async loadFeatureFlags(): Promise<void> {
    this.loading = true;
    try {
      const result = await this.featureFlagsService.fetchAllFeatureFlags(this.dealerId);
      const flags: { [key: string]: boolean } = { ...((result?.flags as any) ?? {}) };
      if (typeof flags[FEATURE_FLAGS.BANNERS_ENABLED] !== 'boolean') {
        flags[FEATURE_FLAGS.BANNERS_ENABLED] = true;
      }
      if (typeof flags[FEATURE_FLAGS.NOTIFICATIONS_ENABLED] !== 'boolean') {
        const legacyValue = flags[FEATURE_FLAGS.LEGACY_NAFA_CAM_SPEC_NOTIFICATIONS_ENABLED];
        flags[FEATURE_FLAGS.NOTIFICATIONS_ENABLED] = typeof legacyValue === 'boolean' ? legacyValue : true;
      }
      delete flags[FEATURE_FLAGS.LEGACY_NAFA_CAM_SPEC_NOTIFICATIONS_ENABLED];
      this.featureFlagKeys = Object.keys(flags)
        .filter((key) => !this.reservedFlagKeys.includes(key))
        .sort((leftKey, rightKey) => {
          const leftLabel = this.formatFlagLabel(leftKey);
          const rightLabel = this.formatFlagLabel(rightKey);
          const labelOrder = leftLabel.localeCompare(rightLabel, undefined, { sensitivity: 'base' });
          return labelOrder !== 0 ? labelOrder : leftKey.localeCompare(rightKey);
        });
      const controls: { [key: string]: any } = {};
      this.featureFlagKeys.forEach((key) => {
        controls[key] = this.fb.control(!!flags[key]);
      });
      this.initialFlags = {};
      this.featureFlagKeys.forEach((key) => {
        this.initialFlags[key] = !!flags[key];
      });
      this.featureFlagsForm = this.fb.group(controls);
    } catch {
      this.alertService.showAlert({
        type: AlertType.critical,
        content: 'Failed to load feature flags.'
      } as Alert);
    } finally {
      this.loading = false;
    }
  }

  async save(): Promise<void> {
    this.saving = true;
    try {
      const formValue: { [key: string]: boolean } = this.featureFlagsForm.value;
      // Merge with the last-known full flags snapshot so saving from this tab
      // never clobbers reserved flags (e.g. notifications) managed elsewhere.
      const currentFlags = (this.store.selectSnapshot(DealerState.featureFlags)?.flags as any) ?? {};
      const merged = { ...currentFlags, ...formValue };
      delete merged[FEATURE_FLAGS.LEGACY_NAFA_CAM_SPEC_NOTIFICATIONS_ENABLED];
      for (const reservedKey of this.reservedFlagKeys) {
        if (typeof currentFlags[reservedKey] === 'boolean') {
          merged[reservedKey] = currentFlags[reservedKey];
        }
      }
      const saved = await this.featureFlagsService.saveFeatureFlags(this.dealerId, merged);
      this.store.dispatch(new UpdateDealerFlagsAction(saved));
      this.initialFlags = { ...formValue };
      this.alertService.showAlert({
        type: AlertType.success,
        content: 'Feature flags saved successfully.'
      } as Alert);
    } catch {
      this.alertService.showAlert({
        type: AlertType.critical,
        content: 'Failed to save feature flags.'
      } as Alert);
    } finally {
      this.saving = false;
    }
  }

  resetChanges(): void {
    this.featureFlagsForm.patchValue(this.initialFlags);
    this.featureFlagsForm.markAsPristine();
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
  }

  isFlagDirty(key: string): boolean {
    const control = this.featureFlagsForm.get(key);
    if (!control) return false;
    return !!control.value !== !!this.initialFlags[key];
  }

  formatFlagLabel(key: string): string {
    if (key === FEATURE_FLAGS.NOTIFICATIONS_ENABLED) {
      return this.translationService.translate('notificationsText');
    }
    return key
      .replace(/^is/, '')
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]+/g, ' ')
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  formatExternalApiConfigLabel(key: string): string {
    const base = key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim();

    const words = base.split(/\s+/).filter(Boolean);
    if (words[0]?.toLowerCase() === 'is' && words.length > 1) {
      words.shift();
    }

    return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }
}
